import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import { TraceWithMessagesFilesAndArtifactsSchema } from '@terra/shared-contracts-trace';
import {
  AgentExecutionBulkBodySchema,
  AgentExecutionBulkResponseSchema,
  AgentExecutionCountQuerySchema,
  AgentExecutionSchema,
  CountByStatusResponseSchema,
  FindByTargetQuerySchema,
  FindByTargetResponseSchema,
} from './agent-execution.schema';
import { AgentExecutionHistoryResponseSchema } from './agent-execution-history.schema';
import { TargetTypeSchema } from '@terra/shared-schemas-ai';
import { z } from 'zod';

export const AgentExecutionContract = terraContract
  .prefix('/agent-execution')
  .router({
    findByTargetType: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/target-type/{targetType}',
        summary: 'Get agent executions by target type with pagination',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ targetType: TargetTypeSchema }),
          query: FindByTargetQuerySchema,
        }),
      )
      .output(FindByTargetResponseSchema),
    getById: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/{executionId}',
        summary: 'Get agent execution by ID',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ executionId: z.string() }) }))
      .output(AgentExecutionSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    getTraces: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/{executionId}/traces',
        summary:
          'Get traces with messages, files, and artifacts for an execution',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ executionId: z.string() }) }))
      .output(z.array(TraceWithMessagesFilesAndArtifactsSchema))
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    getHistory: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/{executionId}/history',
        summary: 'Get execution history with UI-ready messages',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ executionId: z.string() }) }))
      .output(AgentExecutionHistoryResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    findByTarget: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/target/{targetId}',
        summary: 'Get agent executions by target with pagination',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ targetId: z.string() }),
          query: FindByTargetQuerySchema,
        }),
      )
      .output(FindByTargetResponseSchema),
    countByStatus: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/target/{targetId}/count',
        summary: 'Get agent execution count grouped by status',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ targetId: z.string() }),
          query: AgentExecutionCountQuerySchema.optional(),
        }),
      )
      .output(CountByStatusResponseSchema),
    bulkAbort: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/target/{targetId}/bulk-abort',
        summary: 'Abort several agent executions of a target',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ targetId: z.string() }),
          body: AgentExecutionBulkBodySchema,
        }),
      )
      .output(AgentExecutionBulkResponseSchema),
    bulkDelete: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/target/{targetId}/bulk-delete',
        summary: 'Delete several agent executions of a target',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ targetId: z.string() }),
          body: AgentExecutionBulkBodySchema,
        }),
      )
      .output(AgentExecutionBulkResponseSchema),
    markSeen: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'PATCH',
        path: '/{executionId}/seen',
        summary: 'Mark agent execution as seen',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ executionId: z.string() }),
          body: z.object({}),
        }),
      )
      .output(AgentExecutionSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    markArchived: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'PATCH',
        path: '/{executionId}/archived',
        summary: 'Set agent execution archived status',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ executionId: z.string() }),
          body: z.object({ archived: z.boolean() }),
        }),
      )
      .output(AgentExecutionSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    abort: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/{executionId}/abort',
        summary: 'Abort a running agent execution',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ executionId: z.string() }),
          body: z.object({}),
        }),
      )
      .output(z.object({ success: z.boolean() }))
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    pause: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/{executionId}/pause',
        summary: 'Pause a running agent execution',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ executionId: z.string() }),
          body: z.object({}),
        }),
      )
      .output(z.object({ success: z.boolean() }))
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    resume: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/{executionId}/resume',
        summary: 'Resume a blocked agent execution',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ executionId: z.string() }),
          body: z.object({}),
        }),
      )
      .output(z.object({ success: z.boolean() }))
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    resumeDeepActiveProbe: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/{executionId}/deep-active-probe/resume',
        summary:
          'Resume an aborted Deep active probe execution from its last checkpoint',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ executionId: z.string() }),
          body: z.object({}),
        }),
      )
      .output(z.object({ success: z.boolean() })),
    rerunDeepActiveProbe: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/{executionId}/deep-active-probe/rerun',
        summary:
          'Queue a new Deep active probe run on the endpoint of a finished execution',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ executionId: z.string() }),
          body: z.object({}),
        }),
      )
      .output(z.object({ success: z.boolean() })),
    proceed: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/{executionId}/proceed',
        summary:
          'Proceed with a guardrail-blocked execution that completed preparation',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ executionId: z.string() }),
          body: z.object({}),
        }),
      )
      .output(z.object({ success: z.boolean() }))
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    addEvidence: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/{executionId}/evidence',
        summary: 'Add evidence to an agent execution',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ executionId: z.string() }),
          body: z.object({
            artifactId: z.string(),
            description: z.string().min(1),
          }),
        }),
      )
      .output(AgentExecutionSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    updateEvidence: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'PATCH',
        path: '/{executionId}/evidence',
        summary: 'Update evidence description on an agent execution',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ executionId: z.string() }),
          body: z.object({
            artifactId: z.string(),
            description: z.string().min(1),
          }),
        }),
      )
      .output(AgentExecutionSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    removeEvidence: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{executionId}/evidence/{artifactId}',
        summary: 'Remove evidence from an agent execution',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            executionId: z.string(),
            artifactId: z.string(),
          }),
          body: z.object({}),
        }),
      )
      .output(AgentExecutionSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    delete: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{executionId}',
        summary: 'Delete an agent execution',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ executionId: z.string() }),
          body: z.object({}),
        }),
      )
      .output(z.object({ success: z.boolean() })),
  });
